import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { Loader2, XCircle } from 'lucide-react';
import { decodeQrPayload, isPayloadFresh } from '../lib/qrPayload';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';

export const PENDING_MATCH_KEY = 'pendingMatchData';

interface Props {
  user: User | null;
}

type State =
  | { kind: 'starting' }
  | { kind: 'error'; message: string };

export default function MatchPage({ user }: Props) {
  const { data } = useParams<{ data: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'starting' });

  const decoded = useMemo(() => (data ? decodeQrPayload(data) : null), [data]);

  useEffect(() => {
    if (!data) {
      navigate('/', { replace: true });
      return;
    }
    if (!user) {
      localStorage.setItem(PENDING_MATCH_KEY, data);
      navigate('/', { replace: true });
      return;
    }
    localStorage.removeItem(PENDING_MATCH_KEY);
  }, [data, user, navigate]);

  useEffect(() => {
    if (!user || !decoded) return;
    let cancelled = false;
    (async () => {
      if (!isPayloadFresh(decoded)) {
        if (!cancelled) setState({ kind: 'error', message: 'Esse QR expirou. Peça um novo.' });
        return;
      }
      if (decoded.u === user.id) {
        if (!cancelled) setState({ kind: 'error', message: 'Esse é o seu próprio QR.' });
        return;
      }
      const { data: tradeId, error } = await supabase.rpc('start_trade', { p_other: decoded.u });
      if (cancelled) return;
      if (error || !tradeId) {
        setState({ kind: 'error', message: 'Não foi possível iniciar a troca. Tente novamente.' });
        return;
      }
      navigate(`/troca/${tradeId}`, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [user, decoded, navigate]);

  if (!data || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-700">
        <div className="text-white text-center">
          <Loader2 size={32} className="animate-spin mx-auto mb-3" strokeWidth={2.25} />
          <p className="font-body text-small text-white/80">Redirecionando...</p>
        </div>
      </div>
    );
  }

  if (!decoded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-700 px-4">
        <div className="bg-white rounded-2xl border border-ink-300 p-8 mx-4 text-center max-w-sm shadow-md">
          <XCircle size={48} className="mx-auto text-danger mb-4" strokeWidth={1.75} />
          <p className="font-display font-semibold text-h3 text-ink-900 mb-1">QR com problema</p>
          <p className="font-body text-small text-ink-700 mb-5">
            Não foi possível ler esse QR Code. Pode estar incompleto ou com formato diferente.
          </p>
          <Button variant="primary" size="md" onClick={() => navigate('/album')}>
            Ir para o álbum
          </Button>
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-700 px-4">
        <div className="bg-white rounded-2xl border border-ink-300 p-8 mx-4 text-center max-w-sm shadow-md">
          <XCircle size={48} className="mx-auto text-danger mb-4" strokeWidth={1.75} />
          <p className="font-display font-semibold text-h3 text-ink-900 mb-1">Não deu</p>
          <p className="font-body text-small text-ink-700 mb-5">{state.message}</p>
          <Button variant="primary" size="md" onClick={() => navigate('/album?tab=trocas')}>
            Voltar pra Trocas
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-green-700">
      <div className="text-white text-center">
        <Loader2 size={32} className="animate-spin mx-auto mb-3" strokeWidth={2.25} />
        <p className="font-body text-small text-white/80">Iniciando troca...</p>
      </div>
    </div>
  );
}
